<template>
  <div class="user-invite-sender">
    <div class="user-invite-sender-tag-row stretch-row stretch-row-sm">
      <div>
        <vue-tags-input
          class="form-control input-lg user-invite-sender-input"
          v-model="tag"
          :placeholder="
            hasAddressBook ? 'Enter names or email addresses...' : 'Enter email addresses'
          "
          :maxlength="128"
          :max-tags="40"
          :validation="validation"
          :tags="selectedUserTags"
          :autocomplete-items="autocompleteItems()"
          @tags-changed="(newTags) => (selectedUserTags = newTags)"
          :add-on-key="isEmail(tag) ? [13, 9, ':', ';', ' ', ','] : [13, 9, ':', ';']"
          :autocomplete-min-length="0"
          data-test="invite-sender"
        />
      </div>
      <div>
        <button
          class="btn btn-primary btn-lg send-invites-button"
          :disabled="sending"
          @click="send"
          data-test="send-invites-button"
          type="button"
        >
          <span class="hidden-xs">
            {{ sending ? "Sending..." : "Send Invites" }}
          </span>
          <span class="visible-xs-inline-block">
            {{ sending ? "..." : "Send" }}
          </span>
        </button>
      </div>
    </div>
    <p class="text-danger" v-if="sendError">
      There was a problem sending the invites. Please check your internet connection and try again.
    </p>
    <p class="text-danger" v-if="nameError && selectedUserTags.length > 0">
      Sorry, we don't have an email address for {{ nameError }}.
    </p>
    <p class="text-danger" v-if="selectedUserTags.length >= 40">
      Can't send more than 40 invites at once.
    </p>

    <div
      style="margin-top: 10px"
      class="panel panel-default invite-status-panel"
      v-if="sentTags.length > 0 || showExamples"
    >
      <div class="panel-body">
        <p
          v-for="sentTag in sentTags"
          v-bind:key="sentTag.id || sentTag.text"
          class="invite-status-row stretch-row"
        >
          <span class="invite-status-row-text">{{ sentTag.text }}</span>
          <span>
            <span class="invite-status label label-success" v-if="sentTag.inviteDone"
              >Completed</span
            >
            <span class="invite-status label label-warning" v-if="!sentTag.inviteDone">Sent</span>
          </span>
        </p>
        <template v-if="showExamples">
          <p
            v-for="exampleText in exampleTags"
            v-bind:key="exampleText"
            class="invite-status-row stretch-row example-invite-row"
          >
            <span class="invite-status-row-text">{{ exampleText }}</span>
          </p>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import axios from "axios";
import VueTagsInput from "@sipec/vue3-tags-input";
import UserTag from "../user-tag";
import { EmailRegEx } from "./useInviteControlBase";

const props = withDefaults(
  defineProps<{
    userTags: UserTag[];
    sendUrl: string;
    showExamples: boolean;
  }>(),
  {
    showExamples: true,
    userTags: () => [] as UserTag[],
  },
);

const emit = defineEmits(["send-started"]);

const tag = ref("");

const selectedUserTags = ref<UserTag[]>([]);

const sendError = ref(false);
const nameError = ref<string | null>(null);
const sending = ref(false);

function isEmail(email: string): boolean {
  return EmailRegEx.test(email ? email.trim() : email);
}

const hasAddressBook = computed(() => props.userTags.some((t) => !!t.id));

const sentTagValues = ref<UserTag[] | null>(null);

function markAsSent(sentTag: UserTag): void {
  sentTag.inviteSent = true;
  if (!sentTagValues.value!.some((t) => (t.id || t.text) === (sentTag.id || sentTag.text))) {
    sentTagValues.value!.push(sentTag);
  }
}

// Initialize sentTagValues based on props.userTags
function initializeSentTagValues() {
  if (!sentTagValues.value) {
    sentTagValues.value = [];
    props.userTags.filter((t) => t.inviteSent && t.text).forEach((t) => markAsSent(t));
  }
}

// Call the initialization function when component is created
initializeSentTagValues();

// Watch for changes in userTags and update sentTagValues accordingly
watch(
  () => props.userTags,
  (newTags) => {
    sentTagValues.value = [];
    newTags.filter((t) => t.inviteSent && t.text).forEach((t) => markAsSent(t));
  },
  { immediate: true },
);

const sentTags = computed(() => {
  return sentTagValues.value ?? [];
});

const exampleTags = computed(() => {
  const examples: string[] = [];
  for (let i = sentTags.value.length + 1; i <= 8; i++) {
    examples.push(`Invite person ${i}`);
  }
  return examples;
});

function isValidTag(userTag: UserTag): boolean {
  return !!userTag.id || isEmail(userTag.text);
}

const validation = [
  {
    classes: "tag-is-invalid",
    rule: (userTag: any): boolean => !isValidTag(userTag),
  },
];

function autocompleteItems(): UserTag[] {
  if ((tag.value || "").length === 0) {
    return props.userTags;
  }

  return props.userTags.filter(
    (i) =>
      (i.text || "").toLowerCase().indexOf((tag.value || "").toLowerCase()) !== -1 ||
      (i.email || "").toLowerCase().indexOf((tag.value || "").toLowerCase()) !== -1,
  );
}

function performSend(): void {
  if (sending.value) {
    return;
  }

  const tagsToSend: UserTag[] = [];

  selectedUserTags.value.forEach((sut) => {
    sut.text = sut.text ? sut.text.trim() : sut.text;
    sut.email = sut.email ? sut.email.trim() : sut.email;

    if (isValidTag(sut)) {
      tagsToSend.push(sut);
    } else {
      nameError.value = sut.text;
    }
  });

  if (tagsToSend.length === 0) {
    return;
  }

  sending.value = true;
  emit("send-started");

  axios
    .post(
      props.sendUrl,
      tagsToSend.map((t) => t.id || t.text),
    )
    .then((e) => {
      sending.value = false;
      sendError.value = false;
      tagsToSend.forEach((acceptedTag) => {
        markAsSent(acceptedTag);
      });

      selectedUserTags.value = selectedUserTags.value.filter((ut) => !ut.inviteSent); // Clear selectedUserTags
    })
    .catch((e) => {
      sending.value = false;
      sendError.value = true;
    });
}

function send(): void {
  window.setTimeout(() => performSend(), 1); // The reason for this timeout is the way tag creation works.
  // If we didn't have this timeout, if the last tag was still in "text" form, it wouldn't be sent.
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->

<style scoped lang="less">
.stretch-row {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  min-width: 0;

  & > *:first-child {
    flex-grow: 1;
    min-width: 0;
    margin-right: 5px;
  }
}

.invite-status-row-text {
  overflow: hidden;
  text-overflow: ellipsis;
}

.example-invite-row {
  color: #cccccc;
}

/*
@media (max-width:768px) {
  .stretch-row {
    flex-direction: column;
    justify-content: flex-end;
  }

}

*/

.user-invite-sender-input {
  max-width: 435px !important; //Fix for IE11
}

.user-invite-sender .vue-tags-input {
  font-size: 18px !important;
  input {
    font-size: 18px !important;
  }

  .ti-tag {
    font-size: 18px !important;
    max-width: 95%;
    overflow: hidden;
  }
}

.invite-status-panel {
  max-height: 300px;
  overflow-y: auto;
}

.invite-status-row-text {
  text-overflow: ellipsis;
}

.btn.btn-primary:disabled:hover {
  color: white;
}
</style>
